import { useEffect } from "react";
import { IResourceListProps } from "../../common/ra-types";
import {
  useListContext,
  Filter,
  TextInput,
  CreateButton,
  Datagrid,
  TextField,
  BooleanField,
  useRefresh,
  Record,
  Responsive,
  SimpleList,
  SelectInput,
  useTranslate,
} from "react-admin";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import FolderSpecialOutlinedIcon from "@material-ui/icons/FolderSpecialOutlined";
import { useTheme } from "@material-ui/styles";
import { Theme, Tooltip } from "@material-ui/core";
import {
  TenantRegistrationDto,
  TenantRegistrationStatus,
} from "../../../common/data/service-proxies";
import ColoredStatusField from "../../common/data-grid-fields/ColoredStatusField";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { RejectTenantRegistrationMenuItem } from "./RejectTenantRegistrationMenuItem";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";
import ThumbDownIcon from "@material-ui/icons/ThumbDown";
import { ApproveTenantRegistrationMenuItem } from "./ApproveTenantRegistrationMenuItem";
import { ResendTenantWelcomeEmailMenuItem } from "./ResendTenantWelcomeEmailMenuItem";
import {
  BusinessTwoTone,
  Delete,
  DeleteForever,
  Email,
  RestoreFromTrash,
  SupervisedUserCircleOutlined,
  TableChart,
} from "@material-ui/icons";
import { GrantMasterAccountMenuItem } from "./GrantMasterAccountMenuItem";
import { RevokeMasterAccountMenuItem } from "./RevokeMasterAccountMenuItem";
import { ReassignTenantMenuItem } from "./ReassignTenantMenuItem";
import EmailIcon from "@material-ui/icons/Email";
import PersonIcon from "@material-ui/icons/Person";
import PhoneIcon from "@material-ui/icons/Phone";
import HomeWorkIcon from "@material-ui/icons/HomeWork";

import Icon from "@mdi/react";
import { mdiCalendarEdit } from "@mdi/js";
import { DeactivateTenantRegistrationMenuItem } from "./DeactivateTenantRegistrationMenuItem";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { SuspendOrReactivateMasterAccessMenuItem } from "./SuspendOrReactivateMasterAccessMenuItem";
import { DeleteRegistrationMenuItem } from "./DeleteRegistrationMenuItem";
import { ViewEldUsageByEventsMenuItem } from "./ViewEldUsageByEventsMenuItem";
import EventIcon from "@material-ui/icons/Event";
import ProLogsDateField from "../../common/data-grid-fields/ProLogsDateField";
import { ExportDriversDataMenuItem } from "./ExportDriversDataMenuItem";

interface SimpleListProps {
  record?: Record;
}

interface ActionFieldProps<T> {
  record?: T;
  props?: IResourceListProps;
  permissions?: globalThis.Record<string, boolean>;
}

const getRegistrationStatus = (record?: TenantRegistrationDto) => {
  return record?.tenantRegistrationStatus;
};

const getStatusTextColor = (record: any, theme: Theme) => {
  switch (getRegistrationStatus(record)) {
    case TenantRegistrationStatus.Active:
      return theme.palette.success.contrastText;
    case TenantRegistrationStatus.DeleteRejected:
    case TenantRegistrationStatus.Deleted:
    case TenantRegistrationStatus.RegistrationRejected:
    case TenantRegistrationStatus.Deactivated:
    case TenantRegistrationStatus.DeactivationRejected:
    case TenantRegistrationStatus.ReactivationFailed:
      return theme.palette.error.contrastText;
    case TenantRegistrationStatus.DeletePending:
    case TenantRegistrationStatus.RegistrationPending:
    case TenantRegistrationStatus.DeactivationPending:
      return theme.palette.warning.contrastText;
    default:
      return "auto";
  }
};

const getStatusBackgroundColor = (record: any, theme: Theme) => {
  switch (getRegistrationStatus(record)) {
    case TenantRegistrationStatus.Active:
      return theme.palette.success.main;
    case TenantRegistrationStatus.DeletePending:
    case TenantRegistrationStatus.DeactivationPending:
      return theme.palette.warning.main;
    case TenantRegistrationStatus.Deleted:
    case TenantRegistrationStatus.Deactivated:
      return theme.palette.grey[900];
    case TenantRegistrationStatus.RegistrationPending:
      return theme.palette.info.main;
    case TenantRegistrationStatus.RegistrationRejected:
    case TenantRegistrationStatus.RegistrationFailed:
    case TenantRegistrationStatus.DeleteRejected:
    case TenantRegistrationStatus.DeletionFailed:
    case TenantRegistrationStatus.DeactivationRejected:
    case TenantRegistrationStatus.DeactivationFailed:
    case TenantRegistrationStatus.ReactivationFailed:
      return theme.palette.error.main;

    default:
      return "auto";
  }
};

const resolveStatus = (record?: TenantRegistrationDto) => {
  const stillActiveStatuses: TenantRegistrationStatus[] = [
    TenantRegistrationStatus.Active,
    TenantRegistrationStatus.DeleteRejected,
    TenantRegistrationStatus.DeactivationRejected,
  ];

  const stillActive =
    record?.tenantRegistrationStatus &&
    stillActiveStatuses.includes(record.tenantRegistrationStatus);

  if (stillActive) {
    return TenantRegistrationStatus.Active;
  }

  if (record?.tenantRegistrationStatus) {
    return record.tenantRegistrationStatus;
  }

  return TenantRegistrationStatus.Deleted;
};

const hideManageMenuItems = (record: TenantRegistrationDto) => {
  return !(
    record.tenantRegistrationStatus ===
      TenantRegistrationStatus.DeletePending ||
    record.tenantRegistrationStatus ===
      TenantRegistrationStatus.RegistrationPending ||
    record.tenantRegistrationStatus ===
      TenantRegistrationStatus.DeactivationPending
  );
};

const ListActions = () => {
  const { basePath } = useListContext();
  const permissions = useActionPermissions();
  return (
    <>
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.create] ? (
        <CreateButton basePath={basePath} />
      ) : null}
    </>
  );
};

const ListItemActions = <T extends TenantRegistrationDto & Record>({
  record,
  permissions,
  ...props
}: ActionFieldProps<T>) => {
  const refresh = useRefresh();
  const translate = useTranslate();
  const canViewEldDeviceUsage =
    !!permissions &&
    permissions[permissionNames.admin.tenantRegistrations.viewEldDeviceUsage];

  const hasCreatedTenantId = (record: TenantRegistrationDto) =>
    !!record.createdTenantId?.trim();

  return (
    <ActionsField
      {...props}
      record={record}
      hasShow={canViewEldDeviceUsage}
      viewLabel="View ELD devices usage"
      editable={(record) =>
        record.editable &&
        !!permissions &&
        permissions[permissionNames.admin.tenantRegistrations.update]
      }
    >
      {canViewEldDeviceUsage ? (
        <ViewEldUsageByEventsMenuItem
          hidden={(record) => !record.createdTenantId}
          label="View ELD usage by events"
          icon={<EventIcon />}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.main] ? (
        <ExportDriversDataMenuItem
          hidden={(record) =>
            !hasCreatedTenantId(record) || !hideManageMenuItems(record)
          }
          label="Export drivers data"
          icon={<TableChart />}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.deactivate] ? (
        <DeactivateTenantRegistrationMenuItem
          icon={<Delete />}
          label="Deactivate"
          hidden={(record) =>
            !record.editable ||
            resolveStatus(record) !== TenantRegistrationStatus.Active
          }
          onSuccess={refresh}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <ApproveTenantRegistrationMenuItem
          icon={<RestoreFromTrash />}
          label="Reactivate"
          status={TenantRegistrationStatus.Active}
          hidden={(record) =>
            record.tenantRegistrationStatus !==
              TenantRegistrationStatus.Deactivated &&
            record.tenantRegistrationStatus !==
              TenantRegistrationStatus.ReactivationFailed
          }
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <ApproveTenantRegistrationMenuItem
          icon={<DeleteForever />}
          label="Delete"
          status={TenantRegistrationStatus.Deleted}
          hidden={(record) =>
            record.tenantRegistrationStatus !==
              TenantRegistrationStatus.Deactivated &&
            record.tenantRegistrationStatus !==
              TenantRegistrationStatus.ReactivationFailed
          }
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <DeleteRegistrationMenuItem
          label="Delete"
          status={TenantRegistrationStatus.RegistrationRejected}
          confirmTitle={translate(
            "resources.master-tenant-registrations.labels.delete_title",
          )}
          confirmContent={translate(
            "resources.master-tenant-registrations.labels.delete_content",
          )}
          hidden={(record) =>
            record.tenantRegistrationStatus !==
            TenantRegistrationStatus.RegistrationRejected
          }
          icon={<DeleteForever />}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <ApproveTenantRegistrationMenuItem
          label="resources.master-tenant-registrations.labels.approve"
          hidden={hideManageMenuItems}
          icon={<ThumbUpIcon />}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <RejectTenantRegistrationMenuItem
          label="resources.master-tenant-registrations.labels.reject"
          hidden={hideManageMenuItems}
          icon={<ThumbDownIcon />}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <ResendTenantWelcomeEmailMenuItem
          hidden={(record) =>
            !record.createdTenantId ||
            resolveStatus(record) !== TenantRegistrationStatus.Active
          }
          label="Resend welcome e-mail"
          icon={<Email />}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <GrantMasterAccountMenuItem
          hidden={(record) =>
            record.hasMasterAccount ||
            !record.createdTenantId ||
            resolveStatus(record) !== TenantRegistrationStatus.Active
          }
          label="Grant master account"
          icon={<SupervisedUserCircleOutlined />}
          onSuccess={refresh}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <RevokeMasterAccountMenuItem
          hidden={(record) =>
            !record.hasMasterAccount ||
            !record.createdTenantId ||
            resolveStatus(record) !== TenantRegistrationStatus.Active
          }
          label="Revoke master account"
          icon={<SupervisedUserCircleOutlined />}
          onSuccess={refresh}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] ? (
        <ReassignTenantMenuItem
          hidden={(record) =>
            record.hasMasterAccount ||
            !record.createdTenantId ||
            resolveStatus(record) !== TenantRegistrationStatus.Active
          }
          label="Reassign customer..."
          icon={<BusinessTwoTone />}
          onSuccess={refresh}
        />
      ) : null}
      {!!permissions &&
      permissions[permissionNames.admin.tenantRegistrations.manage] &&
      record?.hasMasterAccount ? (
        <SuspendOrReactivateMasterAccessMenuItem
          hidden={(record) =>
            !record.hasMasterAccount ||
            !record.createdTenantId ||
            resolveStatus(record) !== TenantRegistrationStatus.Active
          }
          onSuccess={refresh}
        />
      ) : null}
    </ActionsField>
  );
};

const ListFilter = (props: any) => {
  const { loading } = useListContext();
  useSearchFields(props, ["name"]);
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
      <SelectInput
        className="filter-box"
        label="Status"
        source="tenantRegistrationStatus"
        alwaysOn
        choices={[
          { id: TenantRegistrationStatus.Active, name: "Active" },
          { id: TenantRegistrationStatus.Deactivated, name: "Deactivated" },
          { id: TenantRegistrationStatus.Deleted, name: "Deleted" },
        ]}
      />
    </Filter>
  );
};

export const TenantRegistrationsList = (props: IResourceListProps) => {
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();
  const refresh = useRefresh();
  const { hub } = useSelector((x: AppState) => x.fleetManagerHub);

  useEffect(() => {
    if (!hub) {
      return;
    }

    hub.invoke("SubscribeToTenantRegistrationUpdates");
    hub.on("onTenantRegistrationsUpdated", refresh);
    return () => {
      hub.off("onTenantRegistrationsUpdated");
      hub.invoke("UnsubscribeFromTenantRegistrationUpdates");
    };
  }, [refresh, hub]);

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <TextField
            source="name"
            label="resources.master-tenants.labels.name"
          />

          {permissions &&
          permissions[permissionNames.admin.tenantRegistrations.manage] ? (
            <BooleanField source="hasMasterAccount" label="Master access" />
          ) : null}
        </div>
        <ColoredStatusField
          record={record}
          className="centered-content"
          headerClassName="centered-content"
          color={(record, _, __) => getStatusTextColor(record, theme)}
          backgroundColor={(record, _, __) =>
            getStatusBackgroundColor(record, theme)
          }
          label="resources.master-tenant-registrations.labels.status"
          applyColorTo="everything"
          labelStyle="columns"
          source="tenantRegistrationStatus"
          labelTranslationNamespace="resources.master-tenant-registrations.statuses"
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flexWrap: "wrap",
          marginTop: "10px",
        }}
      >
        <Tooltip title="Contact e-mail">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <EmailIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <TextField
              source="customerContactEmail"
              label="resources.master-tenants.labels.customerContactEmail"
            />
          </div>
        </Tooltip>
        <Tooltip title="Contact person">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <PersonIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <TextField
              source="customerContactName"
              label="resources.master-tenants.labels.customerContactName"
            />
          </div>
        </Tooltip>
        <Tooltip title="Contact phone">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <PhoneIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <TextField
              source="customerContactPhone"
              label="resources.master-tenants.labels.customerContactPhone"
            />
          </div>
        </Tooltip>
        <Tooltip title="Last status change">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <Icon
              style={{
                color: theme.palette.success.main,
              }}
              path={mdiCalendarEdit}
              size={1}
            />

            <ProLogsDateField
              showTime
              source="statusTimestamp"
              label="resources.master-tenant-registrations.labels.statusTimestamp"
            />
          </div>
        </Tooltip>
        {permissions &&
        permissions[permissionNames.admin.tenantRegistrations.manage] ? (
          <Tooltip title="Owner">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
              }}
            >
              <HomeWorkIcon
                style={{
                  color: theme.palette.success.main,
                }}
              />

              <TextField
                source="creatorTenantName"
                label="Owner"
                emptyText="-"
              />
            </div>
          </Tooltip>
        ) : null}
      </div>
    );
  };

  return (
    <div className="content-container">
      <DefaultProLogsList
        empty={<ProLogsEmpty iconType={FolderSpecialOutlinedIcon} />}
        className="content-list"
        title="resources.master-tenant-registrations.name"
        bulkActionButtons={false}
        actions={<ListActions />}
        canExport={false}
        filters={<ListFilter />}
        sort={{ field: "name", order: "asc" }}
        {...props}
      >
        <Responsive
          small={
            <SimpleList
              className="simple-list-wrapper"
              linkType="edit"
              primaryText={(record) => (
                <RenderPrimaryTextForSimpleList record={record} />
              )}
              secondaryText={(record) => (
                <RenderSecondaryTextForSimpleList record={record} />
              )}
              rightIcon={(record) => {
                return (
                  <ListItemActions<TenantRegistrationDto & Record>
                    record={record as TenantRegistrationDto & Record}
                    {...props}
                    permissions={permissions}
                  />
                );
              }}
            />
          }
          medium={
            <Datagrid>
              <TextField
                source="name"
                label="resources.master-tenants.labels.name"
              />
              <TextField
                source="customerContactEmail"
                label="resources.master-tenants.labels.customerContactEmail"
              />
              <TextField
                source="customerContactName"
                label="resources.master-tenants.labels.customerContactName"
              />
              <TextField
                source="customerContactPhone"
                label="resources.master-tenants.labels.customerContactPhone"
              />
              <ColoredStatusField
                className="centered-content"
                headerClassName="centered-content"
                color={(record, _, __) => getStatusTextColor(record, theme)}
                backgroundColor={(record, _, __) =>
                  getStatusBackgroundColor(record, theme)
                }
                label="resources.master-tenant-registrations.labels.status"
                applyColorTo="everything"
                labelStyle="columns"
                source="tenantRegistrationStatus"
                labelTranslationNamespace="resources.master-tenant-registrations.statuses"
              />
              <ProLogsDateField
                showTime
                source="statusTimestamp"
                label="resources.master-tenant-registrations.labels.statusTimestamp"
              />
              {permissions &&
              permissions[permissionNames.admin.tenantRegistrations.manage] ? (
                <BooleanField source="hasMasterAccount" label="Master access" />
              ) : null}
              {permissions &&
              permissions[permissionNames.admin.tenantRegistrations.manage] ? (
                <TextField
                  source="creatorTenantName"
                  label="Owner"
                  emptyText="-"
                />
              ) : null}
              <ListItemActions<TenantRegistrationDto & Record>
                {...props}
                permissions={permissions}
              />
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
